/** How the person record's read-only (vendor / derived) fields render, and the value
 *  actions of its editable ones — one module, so a value that names another record offers
 *  the same links everywhere (`userLinks`).
 *
 *  - `userFieldDisplay(key, c)` — the `display` of a vendor / derived key, from the users
 *    detail (catalog `detail_keys`). `null` renders the em dash.
 *  - `userEditableDisplay(key, c)` — the view-mode rendering of an editable field whose
 *    stored value names records with their own links (the account tags).
 *  - `userFieldActions(key, c)` — the value actions of an editable field, from its LIVE
 *    value. */
import { Box } from '@mui/material';
import type { ReactNode } from 'react';

import type { UserRecordEditContext } from '@/api/userRecord';
import type { ConsoleUserDetail } from '@/api/users';
import { TagBadgeSet } from '@/components/TagBadgeSet/TagBadgeSet';
import { tagChipActions } from '@/components/TagBadgeSet/tagChipActions';
import { act, ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import { MONO_SX } from '@/features/bookings/record/panels/displays';

import { emailActions, gridKindOf, otaActions, plainEmailActions, type UserCtx } from '../../userLinks';

export interface UserDisplayContext {
  detail: ConsoleUserDetail;
  editContext: UserRecordEditContext;
  links: UserCtx;
}

export function userLinks(user: ConsoleUserDetail): UserCtx {
  return { tenantId: user.tour_operator?.id ?? null, kind: gridKindOf(user.kind) };
}

function withActions(node: ReactNode, actions: ValueAction[]): ReactNode {
  return actions.length > 0 ? <ValueActions actions={actions}>{node}</ValueActions> : node;
}

export function Mono({ children }: { children: ReactNode }) {
  return (
    <Box component="span" sx={MONO_SX}>
      {children}
    </Box>
  );
}

export const yesNo = (v: boolean | null | undefined): string | null =>
  v === null || v === undefined ? null : v ? 'Yes' : 'No';

/** A value whose only useful action is "copy it". */
export function copyable(v: string | null | undefined, label: string, mono = true): ReactNode {
  if (!v) return null;
  return withActions(mono ? <Mono>{v}</Mono> : v, [act.copy(v, label)]);
}

/** The OTA a guest came through: the agency, its domain, the code and the relay email —
 *  one value, several lines. */
function otaDisplay(d: ConsoleUserDetail, links: UserCtx): ReactNode {
  const a = d.attributes;
  const lines: ReactNode[] = [];
  if (a.ota) lines.push(withActions(`${a.ota.name ?? 'OTA'} (#${a.ota.id})`, otaActions(a.ota)));
  if (a.ota_domain) {
    lines.push(
      <Box key="domain" component="span" sx={{ color: 'text.secondary' }}>
        Domain {a.ota_domain.name ?? `#${a.ota_domain.id}`}
      </Box>,
    );
  }
  if (a.ota_code) {
    lines.push(
      <Box key="code" component="span" sx={{ color: 'text.secondary' }}>
        Code {copyable(a.ota_code, 'Copy OTA code')}
      </Box>,
    );
  }
  if (a.ota_email) {
    lines.push(
      <Box key="email" component="span" sx={{ color: 'text.secondary' }}>
        Relay {withActions(a.ota_email, [...plainEmailActions(a.ota_email), ...emailActions(a.ota_email, links).slice(2)])}
      </Box>,
    );
  }
  if (lines.length === 0) return null;
  return (
    <Box component="span" sx={{ display: 'grid', gap: 0.25 }}>
      {lines.map((l, i) => (
        <Box key={i} component="span">
          {l}
        </Box>
      ))}
    </Box>
  );
}

export function userFieldDisplay(key: string, c: UserDisplayContext): ReactNode | undefined {
  const { detail: d, links } = c;
  switch (key) {
    case 'email_verified':
      return yesNo(d.email_verified);
    case 'ota':
      return otaDisplay(d, links);
    case 'app_platform':
      return d.attributes.platform || null;
    case 'email_is_fake':
      return yesNo(d.attributes.email_is_fake);
    case 'twilio_identity':
      return copyable(d.internal.twilio_identity, 'Copy chat identity');
    default:
      return undefined;
  }
}

export function userEditableDisplay(key: string, c: UserDisplayContext): ReactNode | undefined {
  const { detail: d, links } = c;
  if (key === 'tags') {
    const ids = c.editContext.baselines[`user:${c.editContext.user_id}`]?.tags;
    if (!Array.isArray(ids)) return undefined;
    const keep = new Set(ids);
    const tags = (d.tags ?? []).filter((t) => keep.has(t.id));
    if (tags.length === 0) return null;
    return (
      <TagBadgeSet
        tags={tags}
        maxVisible={12}
        chipActions={(t) => tagChipActions(t, { tenantId: d.tour_operator?.id ?? null, owner: 'user', userKind: links.kind ?? 'guests' })}
      />
    );
  }
  return undefined;
}

/** Value actions of an editable field, built from its live value. */
export function userFieldActions(key: string): ((value: unknown) => ValueAction[]) | undefined {
  switch (key) {
    case 'passport_number':
      return (v) => (typeof v === 'string' && v.trim() ? [act.copy(v.trim(), 'Copy passport number')] : []);
    default:
      return undefined;
  }
}
